import React from "react";
import { createPortal } from "react-dom";
import { Plus, X } from "lucide-react";
import { imageUrl } from "../lib/api";
import { useI18n } from "../i18n";
import { ShareButtons } from "./ShareButtons";
import { ProductReviews } from "./ProductReviews";
import { TierSelector, TerpeneProfile, ConformityCard, EffectsBars } from "./ProductModalSections";

const PLACEHOLDER = "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/yyc18991_IMG_4514.jpeg";

export function ProductModal({ product, onAdd, disabled, onClose }) {
  const { t } = useI18n();
  const inStock = product.in_stock;
  const tiers = Array.isArray(product.tiers) ? product.tiers.filter((x) => x?.label) : [];
  const [selTier, setSelTier] = React.useState(tiers.length ? tiers[0] : null);
  const displayPrice = selTier ? selTier.price : Number(product.price);
  const effects = product.effects && typeof product.effects === "object" ? [
    ["Relaxation", product.effects.relax],
    ["Énergie", product.effects.energy],
    ["Sommeil", product.effects.sleep],
  ].filter(([, v]) => v != null && v !== "") : [];
  const gallery = [product.image_url, ...(Array.isArray(product.gallery) ? product.gallery : [])].filter(Boolean).slice(0, 5);
  const [mainImg, setMainImg] = React.useState(gallery[0] || "");
  const terpenes = Array.isArray(product.terpenes) ? product.terpenes.filter((x) => x?.name) : [];
  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-3 sm:p-4" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div data-testid="product-modal" className="bg-ink-surface border border-zinc-700 w-full max-w-lg max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-20 h-0 flex justify-end">
          <button data-testid="product-modal-close" aria-label="Fermer" onClick={onClose} className="m-3 bg-black/80 border border-zinc-600 p-2 text-white hover:bg-neon-magenta hover:border-neon-magenta transition-colors"><X className="h-5 w-5" /></button>
        </div>
        <div className="relative aspect-square bg-black">
          <img src={imageUrl(mainImg || product.image_url) || PLACEHOLDER} alt={product.name} className="w-full h-full object-cover" onError={(e) => { e.target.src = PLACEHOLDER; }} />
          <span className="absolute top-3 left-3 px-2 py-1 text-[10px] uppercase font-bold text-neon-magenta border border-neon-magenta rounded-full bg-black/60">{product.category}</span>
          {product.promo && (
            <span className="absolute bottom-3 left-3 px-2.5 py-1 text-xs uppercase font-bold text-black bg-neon-magenta rounded-full glow-magenta">{product.promo_pct ? `-${Math.round(product.promo_pct)}%` : t("product.promo")}</span>
          )}
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-2 p-2 bg-black" data-testid="product-gallery">
            {gallery.map((g) => (
              <button key={g} type="button" data-testid="gallery-thumb" onClick={() => setMainImg(g)} className={`h-16 w-16 shrink-0 border-2 transition-colors ${mainImg === g ? "border-neon-green" : "border-zinc-800 hover:border-zinc-600"}`}>
                <img src={imageUrl(g)} alt="" className="h-full w-full object-cover" loading="lazy" onError={(e) => { e.target.src = PLACEHOLDER; }} />
              </button>
            ))}
          </div>
        )}
        <div className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-anton text-2xl text-white uppercase leading-tight">{product.name}</h3>
            <div className="text-right shrink-0">
              <span className="font-anton text-2xl text-neon-green block" data-testid="modal-price">{displayPrice.toFixed(2)}€</span>
              {selTier && (selTier.units || 1) > 1 && <span className="text-zinc-500 text-xs block" data-testid="modal-price-per-unit">{(selTier.price / selTier.units).toFixed(2)}€ / g</span>}
              {!selTier && product.promo && product.original_price > product.price && (
                <span className="text-xs text-zinc-500 line-through">{Number(product.original_price).toFixed(2)}€</span>
              )}
              <span className="inline-block border border-neon-green/40 text-neon-green text-[9px] uppercase font-bold px-1.5 py-0.5 mt-1">TVA & livreur inclus</span>
            </div>
          </div>

          {tiers.length > 0 && <TierSelector tiers={tiers} selTier={selTier} onSelect={setSelTier} />}

          <div className="flex flex-wrap gap-2">
            {product.best_seller && <span className="text-xs uppercase font-bold text-black bg-neon-green px-2 py-1">🔥 {t("product.best")}</span>}
            {product.lab_url && (
              <a href={imageUrl(product.lab_url)} target="_blank" rel="noopener noreferrer" className="text-xs uppercase font-bold text-neon-magenta border border-neon-magenta/40 px-2 py-1 hover:bg-neon-magenta hover:text-white transition-colors">🔬 {t("product.lab")}</a>
            )}
          </div>

          {terpenes.length > 0 && <TerpeneProfile terpenes={terpenes} total={product.terpenes_total} />}

          <ConformityCard product={product} />

          {effects.length > 0 && <EffectsBars effects={effects} />}

          {(product.long_description || product.description) && (
            <p data-testid="modal-long-desc" className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">{product.long_description || product.description}</p>
          )}

          {product.usage_tips && (
            <div className="border border-zinc-800 bg-ink p-3">
              <p className="text-neon-green text-xs uppercase font-bold mb-1">💡 {t("product.tips")}</p>
              <p data-testid="modal-tips" className="text-zinc-400 text-sm">{product.usage_tips}</p>
            </div>
          )}

          <ShareButtons
            url={`${window.location.origin}/api/share/produit/${window.location.pathname.split("/")[2]}/${product.id}`}
            title={`${product.name} — ${Number(product.price).toFixed(2)}€ sur STUFF CBD 🌿 Livraison rapide, paiement cash à la livraison.`}
          />

          <ProductReviews productId={product.id} />

          {!inStock ? (
            <div className="text-center py-2">
              {product.restocking ? (
                <span className="text-neon-green font-anton uppercase text-sm border border-neon-green px-4 py-2 inline-block">🔄 {t("product.restocking")}</span>
              ) : (
                <span className="text-zinc-400 font-anton uppercase text-sm border border-zinc-600 px-4 py-2 inline-block">{t("product.out")}</span>
              )}
            </div>
          ) : (
            <>
            <button
              data-testid="modal-add-btn"
              disabled={disabled}
              onClick={() => { onAdd(selTier ? { ...product, selectedTier: selTier } : product); onClose(); }}
              className="w-full flex items-center justify-center gap-2 bg-neon-green text-black font-anton uppercase text-lg py-3.5 hover:bg-neon-greenDark transition-colors disabled:opacity-30"
            >
              <Plus className="h-5 w-5" /> {t("product.add")} — {displayPrice.toFixed(2)}€
            </button>
            <div className="flex items-center justify-center gap-4 mt-2 text-zinc-500 text-[11px] uppercase font-bold">
              <span>🛡️ Scellé inviolable</span>
              <span>💶 Paiement à la remise (carte ou espèces)</span>
            </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
